import { Box, Button } from '@mui/material';
import Image from 'next/image';

import { styled } from '@mui/material/styles';

import { LargeTimeUnitEvent } from 'components/Giveaway/components/TimeUnit';
import { useCountdownEnd } from 'hooks/useCountdown';
import { useGetActiveEventQuery } from 'store/rtk/events/service';
import { motion } from 'framer-motion';
import Link from 'next/link';

import EventCases from 'components/Event/Cases/EventCases';
import SiteContainer from 'components/SiteContainer';

import BgImg from 'assets/event/main/background.webp';
import FrontImg from 'assets/event/main/front.webp';
import CasesContainer from '../CasesContainer';
import { EVENT_COLOR } from 'constants/event/details';

const EventContainer = () => {
  const { data: event } = useGetActiveEventQuery();

  if (!event) return null;

  return (
    <>
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        height={{ phxs: 660, tbxs: 720, tblg: 740, lp: 760, lplg: 900 }}
        position='relative'
        justifyContent='space-between'
        sx={{
          background: '#1D1D1B',
          zIndex: 0,
          boxShadow: '0px 0px 100px 100px #1D1D1B'
        }}
      >
        <Box
          width={1}
          height={{ phxs: 460, tbxs: 550, tblg: 450, lp: 3 / 5, lplg: 670 }}
          maxWidth='dtlg'
          position='absolute'
          sx={{
            img: {
              objectPosition: {
                phxs: '20% center',
                lp: 'center'
              }
            }
          }}
        >
          {BgImg ? (
            <Image
              priority
              unoptimized
              loading='eager'
              alt={'event-bg'}
              src={BgImg}
              key={`next-image-event-main-background`}
              fill
              style={{
                objectFit: 'cover',
                width: '100%',
                height: '100%',
                display: 'block',
                zIndex: 0
              }}
            />
          ) : null}

          <SiteContainer>
            <Box width={1} height={1} position='relative' overflow='hidden'>
              <Box
                zIndex={1}
                top={32}
                width={1}
                display='flex'
                flexDirection='column'
                justifyContent={{ phxs: 'flex-start' }}
                alignItems={{
                  phxs: 'center',
                  tbxs: 'flex-start'
                }}
                gap={2}
                overflow='hidden'
              >
                <Box
                  height={{ phxs: 260, phlg: 300, lplg: 430 }}
                  width={{ phxs: 1, tbxs: 600, lp: 800 }}
                  display='flex'
                  flexDirection='column'
                  justifyContent='space-between'
                  alignItems='center'
                  position='relative'
                  component={motion.div}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {FrontImg ? (
                    <Image
                      priority
                      unoptimized
                      loading='eager'
                      alt={'event-front'}
                      src={FrontImg}
                      key={`next-image-event-main-front`}
                      fill
                      style={{
                        objectFit: 'contain',
                        width: '100%',
                        height: '100%',
                        display: 'block'
                      }}
                    />
                  ) : null}
                </Box>

                <Box
                  zIndex={2}
                  display='flex'
                  justifyContent='center'
                  flexDirection={{ phxs: 'column', phlg: 'row' }}
                  gap={1}
                  ml={{ phxs: 0, tbxs: 15, lp: 26 }}
                >
                  <EventButton />
                  <DialogCountdown endDate={event?.event?.eventEnd} />
                </Box>
              </Box>
            </Box>
          </SiteContainer>
        </Box>

        <Box display='flex' width={1} height={1} alignItems='flex-end'>
          <SiteContainer>
            <EventCases cases={event?.cases?.main} />
          </SiteContainer>
        </Box>
      </Box>

      {event?.cases?.special?.length > 0 ? (
        <SiteContainer>
          <Box
            display='flex'
            flexDirection='column'
            gap={{
              phxs: 3,
              tbxs: 6
            }}
            width={1}
          >
            <CasesContainer title={'special cases'} list={event?.cases?.special} isNew={true} />
          </Box>
        </SiteContainer>
      ) : null}
      {/* </motion.div> */}
    </>
  );
};

export default EventContainer;

const EventButton = () => {
  return (
    <Button
      variant='contained'
      size='large'
      component={Link}
      href='/event'
      sx={{
        whiteSpace: 'nowrap',
        backgroundColor: EVENT_COLOR,
        ':hover': {
          backgroundColor: EVENT_COLOR,
          filter: 'brightness(1.2)'
        }
      }}
    >
      Join event
    </Button>
  );
};

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.greyTransparency[700],
  backdropFilter: 'blur(12px)'
}));
const COUNTDOWN_SIZE = 56;

const DialogCountdown = ({ endDate }) => {
  const durations = useCountdownEnd({ endDate });

  return (
    <Box
      display='flex'
      gap={0.5}
      justifyContent='center'
      width='fit-content'
      height={COUNTDOWN_SIZE}
    >
      <StyledBox p={1} width={COUNTDOWN_SIZE} borderRadius={2}>
        <LargeTimeUnitEvent name={'days'} value={durations?.days} />
      </StyledBox>

      <StyledBox p={1} width={COUNTDOWN_SIZE} borderRadius={2}>
        <LargeTimeUnitEvent name={'hours'} value={durations?.hours} />
      </StyledBox>

      <StyledBox p={1} width={COUNTDOWN_SIZE} borderRadius={2}>
        <LargeTimeUnitEvent name={'minutes'} value={durations?.minutes} />
      </StyledBox>

      <StyledBox p={1} width={COUNTDOWN_SIZE} borderRadius={2}>
        <LargeTimeUnitEvent name={'seconds'} value={durations?.seconds} />
      </StyledBox>
    </Box>
  );
};
